import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { flagOf } from '../flags';
import LinkButton from './LinkButton';
import { makeMatcher } from '../searchMatch';

// Header quest picker as one cascading menu: country › city › quest, like a
// macOS menu bar submenu. Countries and cities only expand (on hover, or on
// tap where there is no hover); picking a quest is the only action.
// Quests without a country are grouped under "Other". With `hrefFor`, each
// quest is a link, so a middle click opens it in a new tab.
//
// A search box sits on top: typing replaces the cascade with one flat list
// of matching quests ("flag City › Quest"), found by quest name, id, city or
// country — in the typed keyboard layout or the swapped one (searchMatch).

const OTHER_LABEL = 'Other';
const COL_WIDTH = 170;
const QUEST_COL_WIDTH = 260;
const SEARCH_WIDTH = 320;

const capitalize = (str) => {
  if (!str) return '—';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// '' (no country / no city) sorts last, the rest alphabetically.
const byLabel = (a, b) => (a === '') - (b === '') || a.localeCompare(b);

// [{ country, iso, cities: [{ city, quests: [quest] }] }]
const buildTree = (quests) => {
  // A quest without a country borrows it from another quest in the same city
  // (e.g. a fresh pipeline draft added to the list before a reload).
  const countryByCity = new Map();
  const isoByCountry = new Map();
  quests.forEach(q => {
    if (q.city && q.country) countryByCity.set(q.city, q.country);
    if (q.country && q.iso_code) isoByCountry.set(q.country, q.iso_code);
  });

  const countries = new Map();
  quests.forEach(q => {
    const city = q.city || '';
    const country = q.country || countryByCity.get(city) || '';
    if (!countries.has(country)) countries.set(country, new Map());
    const cities = countries.get(country);
    if (!cities.has(city)) cities.set(city, []);
    cities.get(city).push(q);
  });

  return [...countries.keys()].sort(byLabel).map(country => {
    const cities = countries.get(country);
    return {
      country,
      iso: isoByCountry.get(country) || null,
      cities: [...cities.keys()].sort(byLabel).map(city => ({
        city,
        quests: cities.get(city).slice().sort((a, b) =>
          (a.quest_name || '').localeCompare(b.quest_name || '')),
      })),
    };
  });
};

// Every quest with its place in the tree, in menu order.
const flattenTree = (tree) => {
  const out = [];
  tree.forEach(g => g.cities.forEach(c => c.quests.forEach(quest => {
    out.push({ country: g.country, iso: g.iso, city: c.city, quest });
  })));
  return out;
};

const searchTree = (tree, query) => {
  const match = makeMatcher(query);
  const scored = [];
  flattenTree(tree).forEach((row, order) => {
    const score = match([row.quest.quest_name, row.quest.quest_id, row.city, row.country]);
    if (score) scored.push({ row, score, order });
  });
  scored.sort((a, b) => b.score - a.score || a.order - b.order);
  return scored.map(s => s.row);
};

const QuestMenu = ({ quests = [], value, onSelect, hrefFor }) => {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState([]); // [country] or [country, city] expanded
  const [pos, setPos] = useState(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1); // highlighted search result
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const tree = buildTree(quests);
  const searching = query.trim().length > 0;
  const results = searching ? searchTree(tree, query) : [];

  // Where the selected quest sits in the tree.
  const selected = (() => {
    for (const g of tree) {
      for (const c of g.cities) {
        const quest = c.quests.find(q => q.quest_id === value);
        if (quest) return { country: g.country, iso: g.iso, city: c.city, quest };
      }
    }
    return null;
  })();

  const buttonLabel = selected
    ? `${flagOf(selected.iso, !!selected.country)} › ${capitalize(selected.city)} › ${selected.quest.quest_name}`
    : 'Choose a quest';

  const close = () => { setOpen(false); setPath([]); setQuery(''); setActive(-1); };

  const toggle = () => {
    if (open) { close(); return; }
    const r = buttonRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - COL_WIDTH - 8));
    // Not enough room for both submenus on the right — slide them out to the left.
    const flip = left + COL_WIDTH * 2 + QUEST_COL_WIDTH + 16 > window.innerWidth - 8;
    setPos({ top: r.bottom + 4, left, flip });
    setPath(selected ? [selected.country, selected.city] : []);
    setQuery('');
    setActive(-1);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onResize = () => close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // The cursor goes to the search box as soon as the menu opens.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const el = menuRef.current && menuRef.current.querySelector('.quest-menu-item.active');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (questId) => { onSelect(questId); close(); };

  const onQueryChange = (e) => {
    setQuery(e.target.value);
    setActive(e.target.value.trim() ? 0 : -1);
  };

  // Keys while the search box has the cursor. Escape with text in the box
  // only clears it; the document-level Escape (close) is held back then.
  const onKeyDown = (e) => {
    if (e.key === 'Escape' && searching) {
      e.preventDefault();
      e.stopPropagation();
      setQuery('');
      setActive(-1);
      return;
    }
    if (!searching || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + results.length) % results.length); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const row = results[active >= 0 ? active : 0];
      if (row) pick(row.quest.quest_id);
    }
  };

  const renderResults = () => {
    if (!results.length) return <div className="quest-menu-empty">No quest matches “{query.trim()}”</div>;
    return (
      <div className="quest-menu-results" role="menu">
        {results.map((row, i) => {
          const q = row.quest;
          return (
            <LinkButton
              key={q.quest_id}
              href={hrefFor ? hrefFor(q.quest_id) : undefined}
              role="menuitemradio"
              aria-checked={q.quest_id === value}
              className={`quest-menu-item${q.quest_id === value ? ' selected' : ''}${i === active ? ' active' : ''}`}
              onClick={() => pick(q.quest_id)}
              onMouseEnter={() => setActive(i)}
              title={`${row.country || OTHER_LABEL} › ${capitalize(row.city)} › ${q.quest_name} (${q.quest_id})`}
            >
              <span className="quest-menu-check">{q.quest_id === value ? '✓' : ''}</span>
              <span className="quest-menu-flag">{flagOf(row.iso, !!row.country)}</span>
              <span className="quest-menu-crumb">{capitalize(row.city)} ›</span>
              <span className="quest-menu-text">{q.quest_name}</span>
            </LinkButton>
          );
        })}
      </div>
    );
  };

  const menu = open && pos && ReactDOM.createPortal(
    <div
      ref={menuRef}
      className={`quest-menu${pos.flip ? ' quest-menu-flip' : ''}${searching ? ' quest-menu-searching' : ''}`}
      style={{ top: pos.top, left: pos.left, width: searching ? SEARCH_WIDTH : COL_WIDTH }}
      role="menu"
      onKeyDown={onKeyDown}
    >
      <div className="quest-menu-search">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={onQueryChange}
          placeholder="Search quests…"
          aria-label="Search quests"
          autoComplete="off"
          spellCheck={false}
        />
        {query ? (
          <button
            type="button"
            className="quest-menu-search-clear"
            onClick={() => { setQuery(''); setActive(-1); inputRef.current && inputRef.current.focus(); }}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : null}
      </div>
      {searching && renderResults()}
      {!searching && tree.length === 0 && <div className="quest-menu-empty">No quests</div>}
      {!searching && tree.map(g => {
        const countryOpen = path[0] === g.country;
        return (
          <div
            key={g.country || '__other'}
            className="quest-menu-row"
            onMouseEnter={() => { if (!countryOpen) setPath([g.country]); }}
          >
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={countryOpen}
              className={`quest-menu-item${countryOpen ? ' expanded' : ''}`}
              onClick={() => setPath([g.country])}
            >
              <span className="quest-menu-check">{selected && selected.country === g.country ? '•' : ''}</span>
              <span className="quest-menu-flag">{flagOf(g.iso, !!g.country)}</span>
              <span className="quest-menu-text">{g.country || OTHER_LABEL}</span>
              <span className="quest-menu-arrow">›</span>
            </button>
            {countryOpen && (
              <div className="quest-menu-sub" role="menu" style={{ width: COL_WIDTH }}>
                {g.cities.map(c => {
                  const cityOpen = path[1] === c.city;
                  const holdsSelection = selected && selected.country === g.country && selected.city === c.city;
                  return (
                    <div
                      key={c.city || '__nocity'}
                      className="quest-menu-row"
                      onMouseEnter={() => { if (!cityOpen) setPath([g.country, c.city]); }}
                    >
                      <button
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={cityOpen}
                        className={`quest-menu-item${cityOpen ? ' expanded' : ''}`}
                        onClick={() => setPath([g.country, c.city])}
                      >
                        <span className="quest-menu-check">{holdsSelection ? '•' : ''}</span>
                        <span className="quest-menu-text">{capitalize(c.city)}</span>
                        <span className="quest-menu-arrow">›</span>
                      </button>
                      {cityOpen && (
                        <div className="quest-menu-sub quest-menu-quests" role="menu" style={{ width: QUEST_COL_WIDTH }}>
                          {c.quests.map(q => (
                            <LinkButton
                              key={q.quest_id}
                              href={hrefFor ? hrefFor(q.quest_id) : undefined}
                              role="menuitemradio"
                              aria-checked={q.quest_id === value}
                              className={`quest-menu-item${q.quest_id === value ? ' selected' : ''}`}
                              onClick={() => pick(q.quest_id)}
                              title={`${q.quest_name} (${q.quest_id})`}
                            >
                              <span className="quest-menu-check">{q.quest_id === value ? '✓' : ''}</span>
                              <span className="quest-menu-text">{q.quest_name}</span>
                            </LinkButton>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>,
    document.body
  );

  return (
    <>
      <button
        ref={buttonRef}
        id="quest-select"
        type="button"
        className="quest-menu-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        title={selected ? `${selected.country || OTHER_LABEL} › ${capitalize(selected.city)} › ${selected.quest.quest_name}` : undefined}
      >
        <span className="quest-menu-button-text">{buttonLabel}</span>
        <span className="quest-menu-caret" aria-hidden="true">▾</span>
      </button>
      {menu}
    </>
  );
};

export default QuestMenu;
